import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Clock, MapPin, Users, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMessage } from "@/lib/api";
import { durationLabel } from "@/lib/time";
import { PageTitle, Loading, ErrorBox, FieldError } from "@/components/common";
import { CollectiveParticipants } from "@/components/take5/Participants";

export const selectCls = "h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring";

export default function Take5New() {
  const navigate = useNavigate();
  const [sites, setSites] = useState(null);
  const [durations, setDurations] = useState([]);
  const [members, setMembers] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [location, setLocation] = useState("");
  const [duration, setDuration] = useState("");
  const [error, setError] = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState({ mode: "", team_id: null, participants: [] });
  const patch = (p) => setDraft((x) => ({ ...x, ...p }));

  const load = () => {
    setError("");
    api.get("/referentials").then((r) => {
      setSites(r.data.sites);
      setDurations(r.data.durations || []);
      setMembers(r.data.members || []);
      const saved = localStorage.getItem("safestart.activeSite");
      if (r.data.sites.some((s) => s.id === saved)) setSiteId(saved);
      else if (r.data.sites.length === 1) setSiteId(r.data.sites[0].id);
    }).catch((e) => setError(errMessage(e)));
  };
  useEffect(() => { load(); }, []);

  const next = () => {
    const errs = {};
    if (!siteId) errs.site = "Choisissez le site de la tâche.";
    if (!location.trim()) errs.location = "Précisez le lieu de l'intervention.";
    if (!duration) errs.duration = "Choisissez la durée prévue.";
    setFieldErr(errs);
    if (!Object.keys(errs).length) setStep(2);
  };

  const start = async () => {
    const errs = {};
    if (!draft.mode) errs.mode = "Choisissez Individuel ou Collectif.";
    if (draft.mode === "collectif" && !draft.participants.length) errs.mode = "Indiquez au moins un participant.";
    setFieldErr(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/take5s", { site_id: siteId, location: location.trim(), duration_minutes: Number(duration),
        mode: draft.mode, team_id: draft.mode === "collectif" ? draft.team_id : null, participants: draft.mode === "collectif" ? draft.participants : [] });
      navigate(`/take5/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <PageTitle kicker="Take 5" title="Nouveau Take 5" testId="take5-new-title" />
      {error && <ErrorBox message={error} onRetry={load} />}
      {!sites && !error && <Loading />}
      {sites && step === 1 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <label htmlFor="site" className="text-sm font-semibold">1. Site de la tâche</label>
          <select id="site" data-testid="take5-new-site-select" className={`${selectCls} mt-2`} value={siteId} onChange={(e) => { setSiteId(e.target.value); setFieldErr((f) => ({ ...f, site: "" })); }}>
            <option value="">— Choisir un site —</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <FieldError error={fieldErr.site} testId="take5-new-site-error" />
          <label htmlFor="new-location" className="mt-5 flex items-center gap-1 text-sm font-semibold"><MapPin className="h-4 w-4 text-amber-400" /> 2. Lieu précis</label>
          <Input id="new-location" data-testid="take5-new-location-input" value={location} maxLength={300} onChange={(e) => { setLocation(e.target.value); setFieldErr((f) => ({ ...f, location: "" })); }}
            placeholder="Ex. Bureau 204, 2e étage" className="mt-2 h-12 text-base" />
          <FieldError error={fieldErr.location} testId="take5-new-location-error" />
          <label htmlFor="new-duration" className="mt-5 flex items-center gap-1 text-sm font-semibold"><Clock className="h-4 w-4 text-amber-400" /> 3. Durée prévue de la tâche</label>
          <select id="new-duration" data-testid="take5-new-duration-select" className={`${selectCls} mt-2`} value={duration} onChange={(e) => { setDuration(e.target.value); setFieldErr((f) => ({ ...f, duration: "" })); }}>
            <option value="">— Choisir une durée —</option>
            {durations.map((m) => <option key={m} value={m}>{durationLabel(m)}</option>)}
          </select>
          <FieldError error={fieldErr.duration} testId="take5-new-duration-error" />
          <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> La date et l'heure de début seront enregistrées automatiquement par le serveur au démarrage du Take 5 (hors ligne : heure de l'appareil, signalée comme telle).</p>
          <Button data-testid="take5-new-next-button" onClick={next} className="mt-5 h-12 w-full text-base font-bold">Valider</Button>
        </div>
      )}
      {sites && step === 2 && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="take5-new-mode-step">
          <p className="text-sm text-muted-foreground">{sites.find((s) => s.id === siteId)?.name} · {location.trim()} · {durationLabel(Number(duration))}</p>
          <p className="mt-4 flex items-center gap-1 text-sm font-semibold"><Users className="h-4 w-4 text-amber-400" /> 4. Take 5 individuel ou collectif ?</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[["individuel", "Individuel"], ["collectif", "Collectif"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`take5-new-mode-${v}`} aria-pressed={draft.mode === v}
                onClick={() => { patch({ mode: v }); setFieldErr({}); }}
                className={`min-h-[56px] rounded-md border-2 text-base font-semibold transition-colors ${draft.mode === v ? "border-amber-400 bg-amber-500/15 text-amber-600 dark:text-amber-100" : "border-border text-muted-foreground hover:border-amber-500/60"}`}>{l}</button>
            ))}
          </div>
          {draft.mode === "collectif" && (
            <div className="mt-4">
              <CollectiveParticipants d={draft} set={patch} members={members} />
            </div>
          )}
          <FieldError error={fieldErr.mode} testId="take5-new-mode-error" />
          <div className="mt-5 grid grid-cols-[auto_1fr] gap-2">
            <Button variant="outline" data-testid="take5-new-back-button" onClick={() => setStep(1)} className="h-12"><ArrowLeft className="mr-1 h-4 w-4" /> Retour</Button>
            <Button data-testid="take5-new-start-button" onClick={start} disabled={busy} className="h-12 text-base font-bold">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ouvrir le Take 5
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
